import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import IconButton from '@/ctComponents/IconButton';
import FilterPanel from '@/ctComponents/Filter';
import {
  MyAppointments as MyAppointmentsAction,
  getContainersRedeliveriesReport,
} from '@/actions';
import ReportDroppedInventorySection from './ReportDroppedInventorySection';
import ChartSection from './components/ChartSection';
import OriginSourceOverview from './components/OriginSourceOverview';
import DateRangePicker from './components/DateRangePicker';
import { useConditionalyOutsideAlerter } from '@/hocs';
import {
  REDELIVERY_EMPTY_FILTERS,
  REDELIVERY_SPLIT_VIEW_OPTIONS,
  CHART_CONFIG,
} from './utils/constants';
import { getChartColor } from './utils/colors';
import { createBarDataset, createLineDataset } from './utils/chartHelpers';
import {
  formatDateRangeShort,
  formatDateForInput,
  formatDateForInputTo,
  getCurrentMonth,
  formatDateTimeString,
} from './utils/dateHelpers';
import {
  REDELIVERY_METRIC_DATASETS,
  pickDefaultRedeliveryMetricKey,
} from './utils/datasetConfigs';

const RedeliveryReportTab = () => {
  const getInitialMobileState = () => {
    if (typeof window !== 'undefined') return window.innerWidth <= 1024;

    return false;
  };

  const [activeChart, setActiveChart] = useState('bar'); // 'bar' or 'line'
  const [splitView, setSplitView] = useState('type');
  const [isFullscreen, setIsFullscreen] = useState(getInitialMobileState);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    getInitialMobileState
  );
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [clickedDayIndex, setClickedDayIndex] = useState(null);
  const [clickedBarInfo, setClickedBarInfo] = useState(null);
  const [options, setOptions] = useState({
    open: false,
  });
  const [dateRangePicker, setDateRangePicker] = useState({
    open: false,
    startDate: null,
    endDate: null,
    tempStartDate: null,
    tempEndDate: null,
    filterType: 'DAY',
    tempFilterType: 'DAY',
  });
  const [tempFilters, setTempFilters] = useState(REDELIVERY_EMPTY_FILTERS);
  const [filters, setFilters] = useState(REDELIVERY_EMPTY_FILTERS);
  const [display, setDisplay] = useState(false);
  const [chartDataFromAPI, setChartDataFromAPI] = useState(null);
  const [isLoadingChartData, setIsLoadingChartData] = useState(false);
  const [dateRangePickerApptsTable, setDateRangePickerApptsTable] = useState({
    startDate: null,
    endDate: null,
    tempStartDate: null,
    tempEndDate: null,
  });
  const [hiddenDatasets, setHiddenDatasets] = useState(new Set());
  const [chartWrapperWidth, setChartWrapperWidth] = useState(null);
  const initialTableSelectionDoneRef = useRef(false);

  // Reset hidden datasets when splitView changes
  useEffect(() => {
    setHiddenDatasets(new Set());
  }, [splitView]);

  // Update isFullscreen and isMobileOrTablet based on screen size changes
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileOrTabletSize = window.innerWidth <= 1024;
      setIsMobileOrTablet(isMobileOrTabletSize);
      // Only auto-set fullscreen on initial load, don't override user preference on resize
      if (isMobileOrTabletSize && !isFullscreen) {
        setIsFullscreen(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      const el =
        typeof document !== 'undefined' &&
        document.getElementById('chart-wrapper');
      if (el?.offsetWidth != null) setChartWrapperWidth(el.offsetWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const dateRangePickerRef = useRef(null);
  const wrapperRef = useRef(null);
  const filterButtonRef = useRef(null);
  const myAppointmentsRef = useRef(null);

  useConditionalyOutsideAlerter(
    wrapperRef,
    () => setDisplay(false),
    (event) => {
      // Don't close if clicking on the filter button
      if (
        filterButtonRef.current &&
        filterButtonRef.current.contains(event.target)
      ) {
        return false;
      }
      return true;
    }
  );

  const countSelectedFilters = useCallback((filterObj) => {
    return Object.values(filterObj).filter(
      (arr) => Array.isArray(arr) && arr.length > 0
    ).length;
  }, []);

  // Wrapper for setTempFilters to enforce maximum 2 filters
  const handleSetTempFilters = useCallback(
    (updater) => {
      setTempFilters((prev) => {
        const newFilters =
          typeof updater === 'function' ? updater(prev) : updater;

        const currentCount = countSelectedFilters(newFilters);

        if (currentCount > 2) {
          const addedCategory = Object.keys(newFilters).find((key) => {
            const prevArr = prev[key] || [];
            const newArr = newFilters[key] || [];
            return newArr.length > prevArr.length;
          });

          const categoriesToRemove = ['types', 'shippingLines', 'autobookStatus'];

          let categoryToRemove = null;
          for (const category of categoriesToRemove) {
            if (
              category !== addedCategory &&
              newFilters[category] &&
              newFilters[category].length > 0
            ) {
              categoryToRemove = category;
              break;
            }
          }

          if (categoryToRemove) {
            return {
              ...newFilters,
              [categoryToRemove]: [],
            };
          }
        }

        return newFilters;
      });
    },
    [countSelectedFilters]
  );

  // Handle outside click to close date range picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateRangePicker.open &&
        dateRangePickerRef.current &&
        !dateRangePickerRef.current.contains(event.target)
      ) {
        setDateRangePicker((prev) => ({ ...prev, open: false }));
      }
    };

    if (dateRangePicker.open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateRangePicker.open]);

  const handleDateRangeClick = () => {
    setDateRangePicker({
      ...dateRangePicker,
      open: !dateRangePicker.open,
      tempStartDate: dateRangePicker.startDate,
      tempEndDate: dateRangePicker.endDate,
      tempFilterType: dateRangePicker.filterType,
    });
    setOptions({ ...options, open: false });
  };

  const handleDateRangeChange = useCallback((dates) => {
    const [start, end] = dates;
    setDateRangePicker((prev) => ({
      ...prev,
      tempStartDate: start,
      tempEndDate: end,
      tempFilterType: 'DAY',
    }));
    setDateRangePickerApptsTable((prev) => ({
      ...prev,
      tempStartDate: start,
      tempEndDate: end,
    }));
  }, []);

  const handleFilterTypeChange = useCallback((type) => {
    setDateRangePicker((prev) => ({
      ...prev,
      tempFilterType: type,
    }));
  }, []);

  const handleDateRangeReset = useCallback(() => {
    setDateRangePicker((prev) => ({
      ...prev,
      tempStartDate: null,
      tempEndDate: null,
      startDate: null,
      endDate: null,
      filterType: 'DAY',
      tempFilterType: 'DAY',
      open: false,
    }));
    setDateRangePickerApptsTable({
      startDate: null,
      endDate: null,
    });
  }, []);

  const handleDateRangeApply = useCallback(() => {
    setDateRangePicker((prev) => ({
      ...prev,
      startDate: prev.tempStartDate,
      endDate: prev.tempEndDate,
      filterType: prev.tempFilterType,
      open: false,
    }));
    setDateRangePickerApptsTable((prev) => ({
      ...prev,
      startDate: prev.tempStartDate,
      endDate: prev.tempEndDate,
    }));
  }, []);

  const calculateDateRangeFromClickedBar = useCallback(() => {
    const clickedBarDate = clickedBarInfo?.date;
    const filterType = dateRangePicker.filterType;

    const clickedDate = clickedBarDate;
    const currentFilterType = filterType;

    if (!clickedDate) {
      return { startDate: null, endDate: null };
    }

    let startDate, endDate;

    if (currentFilterType === 'DAY') {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day + 1, 0, 0, 0, 0);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else if (currentFilterType === 'MONTH') {
      const parts = clickedDate.split('-');
      if (parts.length < 2) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      startDate = new Date(year, month, 1, 0, 0, 0, 0);
      endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
    } else if (currentFilterType === 'WEEK') {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);
      endDate.setDate(endDate.getDate() + 6);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else if (
      currentFilterType === 'HOUR' ||
      currentFilterType === 'HOUR4' ||
      currentFilterType === 'HOUR6' ||
      currentFilterType === 'HOUR12'
    ) {
      const parts = clickedDate.split(' ');
      const datePart = parts[0] || clickedDate;
      const timePart = parts[1] || '00';

      // Parse date part manually to avoid timezone issues
      const dateParts = datePart.split('-');
      if (dateParts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2], 10);

      // Determine hours to add based on filter type
      let hoursToAdd = 1; // Default for HOUR
      if (currentFilterType === 'HOUR4') {
        hoursToAdd = 4;
      } else if (currentFilterType === 'HOUR6') {
        hoursToAdd = 6;
      } else if (currentFilterType === 'HOUR12') {
        hoursToAdd = 12;
      }

      let startHour, minutes;

      if (timePart.includes('-')) {
        [startHour, minutes] = timePart.split('-').map(Number);
      } else {
        startHour = Number(timePart);
        minutes = 0;
      }

      if (isNaN(startHour) || (timePart.includes('-') && isNaN(minutes))) {
        startDate = new Date(year, month, day, 0, 0, 0, 0);
        endDate = new Date(year, month, day, hoursToAdd, 0, 0, 0);
      } else {
        startDate = new Date(year, month, day, startHour, minutes || 0, 0, 0);
        endDate = new Date(year, month, day, startHour, minutes || 0, 0, 0);
        endDate.setHours(endDate.getHours() + hoursToAdd);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    } else {
      // Parse date string manually to avoid timezone issues
      const parts = clickedDate.split('-');
      if (parts.length < 3) {
        return { startDate: null, endDate: null };
      }
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);

      startDate = new Date(year, month, day, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { startDate: null, endDate: null };
      }
    }

    return {
      startDate: formatDateTimeString(startDate),
      endDate: formatDateTimeString(endDate),
    };
  }, [clickedBarInfo, dateRangePicker.filterType]);

  useEffect(() => {
    if (!splitView) return;

    const { startDate, endDate } = calculateDateRangeFromClickedBar();

    setDateRangePickerApptsTable({
      startDate,
      endDate,
    });
  }, [clickedBarInfo, splitView, calculateDateRangeFromClickedBar]);

  const chartMetricKey = useMemo(() => {
    const label = clickedBarInfo?.clickedSegment?.label;
    if (!label) return null;
    return (
      REDELIVERY_METRIC_DATASETS.find((d) => d.label === label)?.key || null
    );
  }, [clickedBarInfo?.clickedSegment?.label]);

  const redeliveryTableQuery = useMemo(() => {
    const { startDate, endDate } = dateRangePickerApptsTable;
    if (!startDate || !endDate || !chartMetricKey) {
      return null;
    }

    return {
      dateFrom: formatDateTimeString(startDate),
      dateTill: formatDateTimeString(endDate),
      label: chartMetricKey,
      shipping_lines:
        filters.shippingLines.length > 0
          ? filters.shippingLines.map(({ value }) => value)
          : null,
    };
  }, [
    dateRangePickerApptsTable.startDate,
    dateRangePickerApptsTable.endDate,
    chartMetricKey,
    filters.shippingLines,
  ]);

  const filterValues = useMemo(
    () => ({
      types:
        filters.types.length > 0
          ? filters.types.map(({ value }) => value)
          : null,
      shipping_lines:
        filters.shippingLines.length > 0
          ? filters.shippingLines.map(({ value }) => value)
          : null,
      autobook_statuses:
        filters.autobookStatus.length > 0
          ? filters.autobookStatus.map(({ value }) => value)
          : null,
    }),
    [filters.types, filters.shippingLines, filters.autobookStatus]
  );

  useEffect(() => {
    const dateFrom =
      dateRangePicker.startDate?.toISOString().split('T')[0] || null;
    const dateTill =
      dateRangePicker.endDate?.toISOString().split('T')[0] || null;
    const timeSplit = dateRangePicker.filterType || 'DAY';

    setIsLoadingChartData(true);
    getContainersRedeliveriesReport({
      dateFrom,
      dateTill,
      timeSplit,
      ...filterValues,
    })
      .then((response) => {
        const payload =
          response?.data?.chartData != null
            ? response.data
            : response?.chartData != null
              ? response
              : null;
        if (payload?.chartData) {
          setChartDataFromAPI(payload);
        } else {
          setChartDataFromAPI(null);
        }
        setIsLoadingChartData(false);
      })
      .catch((error) => {
        console.error('getContainersRedeliveriesReport error:', error);
        setChartDataFromAPI(null);
        setIsLoadingChartData(false);
      });
  }, [
    dateRangePicker.startDate,
    dateRangePicker.endDate,
    dateRangePicker.filterType,
    filterValues,
  ]);

  const formatDateLabel = useCallback((label, timeSplit) => {
    if (!label || typeof label !== 'string') {
      return '';
    }

    if (timeSplit === 'MONTH') {
      // Format: "2025-12" -> "December"
      const parts = label.split('-');
      if (parts.length < 2) {
        return label;
      }

      return (
        new Date(label).toLocaleString('en-US', { month: 'long' }) || label
      );
    } else if (timeSplit === 'DAY' || timeSplit === 'WEEK') {
      // Format: "2026-01-03" -> "Sat 26"
      const date = new Date(label);
      if (isNaN(date.getTime())) {
        return label;
      }

      const dayNumber = date.getDate();
      return `${dayNumber}`;
    } else if (
      (typeof timeSplit === 'string' && timeSplit.startsWith('HOUR')) ||
      timeSplit === 'HOUR' ||
      timeSplit === 'HOUR4' ||
      timeSplit === 'HOUR6' ||
      timeSplit === 'HOUR12'
    ) {
      // Format: "2026-01-03 00-06" -> "Sat 26, 00-06"
      const parts = label.split(' ');
      const datePart = parts[0] || label;
      const hourPart = parts[1] || '';

      if (!datePart) {
        return label;
      }

      const date = new Date(datePart);
      if (isNaN(date.getTime())) {
        return label;
      }

      const dayNumber = date.getDate();

      const formattedHourPart = hourPart ? hourPart.replace(/-/g, ':') : '';
      return formattedHourPart ? `${formattedHourPart}` : `${dayNumber}`;
    }

    return label;
  }, []);

  const chartData = chartDataFromAPI?.chartData;
  const timeSplit = chartDataFromAPI?.timeSplit || 'DAY';

  const reversedChartData = useMemo(() => {
    return chartData ? chartData.slice().reverse() : null;
  }, [chartData]);

  const chartLabels = useMemo(() => {
    // Show skeleton placeholders when loading
    if (isLoadingChartData) {
      // Use previous data length if available, otherwise use a default number
      const skeletonCount = reversedChartData?.length || 10;
      return Array(skeletonCount).fill('-');
    }

    if (!reversedChartData || reversedChartData.length === 0) {
      return [];
    }

    return reversedChartData
      .map((item) => formatDateLabel(item?.label, timeSplit))
      .filter((label) => label);
  }, [reversedChartData, timeSplit, formatDateLabel, isLoadingChartData]);

  const originalDateLabels = useMemo(() => {
    // Show skeleton placeholders when loading
    if (isLoadingChartData) {
      // Use previous data length if available, otherwise use a default number
      const skeletonCount = reversedChartData?.length || 10;
      return Array(skeletonCount).fill('-');
    }

    if (!reversedChartData || reversedChartData.length === 0) {
      return [];
    }

    return reversedChartData
      .map((item) => item?.label || '')
      .filter((label) => label);
  }, [reversedChartData, isLoadingChartData]);

  const overviewData = useMemo(() => {
    if (!reversedChartData || reversedChartData.length === 0) {
      return null;
    }

    const datasetConfigs = REDELIVERY_METRIC_DATASETS;
    const days = reversedChartData.map((item) => {
      const metrics = item.data || item.types || {};
      const day = { all: 0 };

      datasetConfigs.forEach(({ key }) => {
        const numeric = Number(metrics[key]) || 0;
        day[key] = numeric;
        day.all += numeric;
      });

      return day;
    });

    const total = days.reduce((acc, day) => {
      const next = { ...acc, all: (acc.all || 0) + (day.all || 0) };
      datasetConfigs.forEach(({ key }) => {
        next[key] = (next[key] || 0) + (day[key] || 0);
      });
      return next;
    }, { all: 0 });

    return { total, days, type: 'type', datasetConfigs };
  }, [reversedChartData]);

  useEffect(() => {
    initialTableSelectionDoneRef.current = false;
  }, [chartDataFromAPI?.chartData, chartDataFromAPI?.dateFrom, chartDataFromAPI?.dateTill]);

  useEffect(() => {
    if (
      initialTableSelectionDoneRef.current ||
      isLoadingChartData ||
      !originalDateLabels?.length ||
      !reversedChartData?.length
    ) {
      return;
    }

    const lastIndex = originalDateLabels.length - 1;
    const dayItem = reversedChartData[lastIndex];
    const metricKey = pickDefaultRedeliveryMetricKey(dayItem);
    const metricConfig = REDELIVERY_METRIC_DATASETS.find(
      (d) => d.key === metricKey
    );

    setClickedBarInfo({
      date: originalDateLabels[lastIndex],
      dataIndex: lastIndex,
      clickedSegment: {
        label: metricConfig?.label || metricKey,
        value: dayItem?.data?.[metricKey] ?? 0,
      },
    });
    setClickedDayIndex(lastIndex);
    setSelectedDayIndex(lastIndex);
    initialTableSelectionDoneRef.current = true;
  }, [originalDateLabels, reversedChartData, isLoadingChartData]);

  const getChartDatasetColor = useCallback(
    (label) => getChartColor(label, splitView),
    [splitView]
  );

  const minChartBars = useMemo(() => {
    const width = chartWrapperWidth ? chartWrapperWidth - 40 : 640;
    return Math.max(1, Math.floor(width / CHART_CONFIG.BAR_THICKNESS));
  }, [chartWrapperWidth]);

  const barChartData = useMemo(() => {
    if (!overviewData?.days) {
      return { labels: chartLabels, datasets: [] };
    }

    const barCount = chartLabels.length;
    const paddedLabels =
      barCount >= minChartBars
        ? chartLabels
        : [...chartLabels, ...Array(minChartBars - barCount).fill('')];

    const datasetConfigs =
      overviewData.datasetConfigs || REDELIVERY_METRIC_DATASETS;

    const datasets = datasetConfigs.map((config) => {
      const values = overviewData.days.map((day) => day[config.key] || 0);
      const paddedData =
        values.length >= minChartBars
          ? values
          : [...values, ...Array(minChartBars - values.length).fill(0)];

      const dataset = createBarDataset(
        config.label,
        getChartDatasetColor(config.label),
        paddedData
      );

      if (hiddenDatasets.has(config.label)) {
        dataset.hidden = true;
      }
      return dataset;
    });

    return { labels: paddedLabels, datasets };
  }, [
    chartLabels,
    overviewData,
    splitView,
    getChartDatasetColor,
    hiddenDatasets,
    minChartBars,
  ]);

  const lineChartData = useMemo(() => {
    if (!overviewData?.days) {
      return { labels: chartLabels, datasets: [] };
    }

    const barCount = chartLabels.length;
    const paddedLabels =
      barCount >= minChartBars
        ? chartLabels
        : [...chartLabels, ...Array(minChartBars - barCount).fill('')];

    const datasetConfigs =
      overviewData.datasetConfigs || REDELIVERY_METRIC_DATASETS;

    const datasets = datasetConfigs.map((config) => {
      const values = overviewData.days.map((day) => day[config.key] || 0);
      const paddedData =
        values.length >= minChartBars
          ? values
          : [...values, ...Array(minChartBars - values.length).fill(0)];

      const dataset = createLineDataset(
        config.label,
        getChartDatasetColor(config.label),
        paddedData
      );
      // Use Chart.js built-in hidden property instead of filtering
      if (hiddenDatasets.has(config.label)) {
        dataset.hidden = true;
      }
      return dataset;
    });

    return { labels: paddedLabels, datasets };
  }, [
    chartLabels,
    overviewData,
    splitView,
    getChartDatasetColor,
    hiddenDatasets,
    minChartBars,
  ]);

  const resetFilters = useCallback(() => {
    setFilters(REDELIVERY_EMPTY_FILTERS);
    setTempFilters(REDELIVERY_EMPTY_FILTERS);
    setSplitView('type');
    localStorage.removeItem('report-redelivery-filters');
    setDisplay(false);
  }, []);

  const getFilter = () => {
    MyAppointmentsAction({
      statuses: [],
      types: [],
      terminals: [],
      shipping_lines: [],
      container_name: [],
    }).then((res) => {
      const transformedArray = [
        {
          category: 'types',
          title: 'Type',
          options:
            res.data.types?.map((t) => ({
              label: t.title,
              value: t.value,
            })) || [],
        },
        {
          category: 'shippingLines',
          title: 'SSL',
          options:
            res.data.shippingLines?.map((s) => ({
              label: s.name,
              value: s.id,
            })) || [],
        },
        {
          category: 'autobookStatus',
          title: 'Autobook Status',
          options: [
            { label: 'On', value: 'ON' },
            { label: 'Off', value: 'OFF' },
          ],
        },
      ];
      setOptions(transformedArray);
    });
  };

  return (
      <div className="">
        <div className="mb-6">
          <h2 className="text-[28px] font-averta-bold text-primary-text font-bold mb-2">
            Redelivery Report
          </h2>
          <p className="text-base font-averta text-inactive-text mt-2">
            Track and analyze your container redeliveries to gain insights and
            manage dropped inventory efficiently.
          </p>
        </div>
        <div className="flex flex-col md:flex-row justify-end md:items-center gap-4 md:gap-0">
          <div className="relative flex flex-wrap gap-3 w-auto justify-between md:justify-start md:ml-auto">
            <DateRangePicker
              dateRangePicker={dateRangePicker}
              setDateRangePicker={setDateRangePicker}
              dateRangePickerRef={dateRangePickerRef}
              handleDateRangeClick={handleDateRangeClick}
              handleDateRangeChange={handleDateRangeChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleDateRangeReset={handleDateRangeReset}
              handleDateRangeApply={handleDateRangeApply}
              formatDateRangeShort={formatDateRangeShort}
              formatDateForInput={formatDateForInput}
              formatDateForInputTo={formatDateForInputTo}
            />
          </div>
        </div>
        <div className={`flex flex-col xl:flex-row gap-4 my-6`}>
          <ChartSection
            activeChart={activeChart}
            setActiveChart={setActiveChart}
            splitView={splitView}
            setSplitView={setSplitView}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            barChartData={barChartData}
            lineChartData={lineChartData}
            getCurrentMonth={getCurrentMonth}
            selectedDayIndex={selectedDayIndex}
            setSelectedDayIndex={setSelectedDayIndex}
            setClickedDayIndex={setClickedDayIndex}
            clickedBarInfo={clickedBarInfo}
            setClickedBarInfo={setClickedBarInfo}
            dateRangePicker={dateRangePicker}
            myAppointmentsRef={myAppointmentsRef}
            filters={filters}
            setTempFilters={setTempFilters}
            setFilters={setFilters}
            originalDateLabels={originalDateLabels}
            chartLabels={chartLabels}
            isLoadingChartData={isLoadingChartData}
            overviewData={overviewData}
            hiddenDatasets={hiddenDatasets}
            setHiddenDatasets={setHiddenDatasets}
            isMobileOrTablet={isMobileOrTablet}
            showQuickFilters={false}
            chartTitle="Redelivery"
            showSplitViewSelector={false}
            splitViewOptions={REDELIVERY_SPLIT_VIEW_OPTIONS}
            setActiveFilter={() => {}}
            showFilterBadges={false}
          />

          {!isFullscreen && (
            <div
              key="right-panel"
              className="flex flex-col gap-4 w-full xl:w-1/3">
              <OriginSourceOverview
                key={splitView}
                overviewData={overviewData}
                splitView={splitView}
                selectedDayIndex={selectedDayIndex}
                chartLabels={chartLabels}
                clickedBarInfo={clickedBarInfo}
                originalDateLabels={originalDateLabels}
                hiddenDatasets={hiddenDatasets}
                setHiddenDatasets={setHiddenDatasets}
                setClickedBarInfo={setClickedBarInfo}
              />
            </div>
          )}
        </div>
        <div id="report-dropped-inventory" className="mt-6" ref={myAppointmentsRef}>
          <ReportDroppedInventorySection tableQuery={redeliveryTableQuery} />
        </div>
      </div>
  );
};

export default RedeliveryReportTab;
